import { yupResolver } from "@hookform/resolvers/yup";
import {
  CourseTabsContent,
  TabConfig,
  BuiltInTabConfig,
  CustomTabConfig,
  CardsTabConfig,
  CardItemConfig,
} from "api/shop";
import { PaziaFormButton, PaziaCheckbox, PaziaSelect } from "components/atoms";
import { Select, IDropdownOption } from "components/atoms/PaziaSelect/PaziaSelect";
import { PaziaQuill } from "components/atoms/PaziaQuill/PaziaQuill";
import { useCourseGroupAttributesAll } from "api/coursegroup-attributes";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
} from "react-beautiful-dnd";
import { MdAdd, MdDelete, MdDragIndicator, MdEdit, MdExpandMore, MdExpandLess } from "react-icons/md";
import * as yup from "yup";

const BUILT_IN_TABS: { id: string; label: string }[] = [
  { id: "trainers", label: "Wykładowcy" },
  { id: "schedule", label: "Harmonogram" },
  { id: "program", label: "Program" },
  { id: "detailed-info", label: "Szczegóły" },
  { id: "contact", label: "Kontakt" },
  { id: "payments", label: "Platnosci" },
  { id: "faq", label: "FAQ" },
];

function normalizeTabs(content: any): CourseTabsContent {
  if (Array.isArray(content?.tabs) && content.tabs.length > 0 && typeof content.tabs[0] === "string") {
    return {
      ...content,
      tabs: content.tabs.map((id: string) => {
        if (id === "cards") {
          return { kind: "cards" as const, id: "cards", label: "Informacje", cards: [] };
        }
        return {
          kind: "built-in" as const,
          id,
          label: BUILT_IN_TABS.find((t) => t.id === id)?.label ?? id,
        };
      }),
    };
  }
  // Migrate old built-in cards tab to cards kind
  if (Array.isArray(content?.tabs)) {
    return {
      ...content,
      tabs: content.tabs.map((t: any) => {
        if (t.kind === "built-in" && t.id === "cards") {
          return { kind: "cards" as const, id: "cards", label: t.label ?? "Informacje", cards: [] };
        }
        return t;
      }),
    };
  }
  return content;
}

function generateCustomId(): string {
  return "custom-" + Math.random().toString(36).substring(2, 8);
}

interface CourseTabsFormProps {
  onSubmit: (data: CourseTabsFormValues) => void;
  onCancel: () => void;
  defaultValues?: CourseTabsContent;
  isLoading?: boolean;
}

export interface CourseTabsFormValues {
  tabs: TabConfig[];
  variant: "scroll" | "tabs";
  showSectionHeaders?: boolean;
  tabsLayout?: "wrap" | "scroll";
}

interface FormValues {
  variant: string;
  showSectionHeaders: boolean;
  tabsLayout: string;
}

const schema = yup
  .object({
    variant: yup.string().required("Wariant jest wymagany"),
    showSectionHeaders: yup.boolean().default(true),
    tabsLayout: yup.string().required(),
  })
  .required();

export const CourseTabsForm = ({ onSubmit, onCancel, defaultValues, isLoading }: CourseTabsFormProps) => {
  const normalized = defaultValues ? normalizeTabs(defaultValues) : undefined;

  const [tabs, setTabs] = useState<TabConfig[]>(
    normalized?.tabs ?? BUILT_IN_TABS.map((t) => ({ kind: "built-in", id: t.id, label: t.label })),
  );
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<string | null>(null);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      variant: normalized?.variant ?? "scroll",
      showSectionHeaders: normalized?.showSectionHeaders ?? true,
      tabsLayout: normalized?.tabsLayout ?? "scroll",
    },
  });

  const handleFormSubmit = (data: FormValues) => {
    onSubmit({
      tabs,
      variant: data.variant as "scroll" | "tabs",
      showSectionHeaders: data.showSectionHeaders,
      tabsLayout: data.tabsLayout as "wrap" | "scroll",
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const newTabs = [...tabs];
    const [moved] = newTabs.splice(result.source.index, 1);
    newTabs.splice(result.destination.index, 0, moved);
    setTabs(newTabs);
  };

  const removeTab = (index: number) => {
    setTabs((prev) => prev.filter((_, i) => i !== index));
    if (tabs[index]?.id === editingTabId) {
      setEditingTabId(null);
    }
  };

  const availableBuiltIns = BUILT_IN_TABS.filter((bt) => !tabs.some((t) => t.kind === "built-in" && t.id === bt.id));

  const addBuiltInTab = (id: string) => {
    const def = BUILT_IN_TABS.find((t) => t.id === id);
    if (!def) return;
    setTabs((prev) => [...prev, { kind: "built-in", id: def.id, label: def.label }]);
  };

  const addCustomTab = () => {
    const newTab: CustomTabConfig = {
      kind: "custom",
      id: generateCustomId(),
      label: "Nowa zakładka",
      content: "",
    };
    setTabs((prev) => [...prev, newTab]);
    setEditingTabId(newTab.id);
  };

  const updateTabLabel = (tabId: string, label: string) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, label } : t)));
  };

  const updateCustomTabContent = (tabId: string, content: string) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId && t.kind === "custom" ? { ...t, content } : t)));
  };

  const hasCardsTab = tabs.some((t) => t.kind === "cards");

  const addCardsTab = () => {
    if (hasCardsTab) return;
    const newTab: CardsTabConfig = {
      kind: "cards",
      id: "cards",
      label: "Informacje",
      cards: [],
    };
    setTabs((prev) => [...prev, newTab]);
    setEditingTabId(newTab.id);
  };

  const addCardItem = (tabId: string) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId && t.kind === "cards"
          ? { ...t, cards: [...t.cards, { header: "", content: "", bgColor: "#F5F5F5", textColor: "#1E1E1E" }] }
          : t,
      ),
    );
  };

  const removeCardItem = (tabId: string, cardIndex: number) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId && t.kind === "cards" ? { ...t, cards: t.cards.filter((_, i) => i !== cardIndex) } : t,
      ),
    );
  };

  const updateCardItem = (
    tabId: string,
    cardIndex: number,
    field: keyof CardItemConfig,
    value: string | number | undefined,
  ) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId && t.kind === "cards"
          ? { ...t, cards: t.cards.map((c, i) => (i === cardIndex ? { ...c, [field]: value } : c)) }
          : t,
      ),
    );
  };

  const updateTabDescription = (tabId: string, description: string) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, description } : t)));
  };

  const updateTabDescriptionAttributeId = (tabId: string, attributeId: number | undefined) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId
          ? { ...t, descriptionAttributeId: attributeId, description: attributeId ? undefined : t.description }
          : t,
      ),
    );
  };

  const enableManualDescription = (tabId: string) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId ? { ...t, description: t.description || "", descriptionAttributeId: undefined } : t,
      ),
    );
  };

  const removeTabDescription = (tabId: string) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId ? { ...t, description: undefined, descriptionAttributeId: undefined } : t,
      ),
    );
  };

  const updateTabDescriptionBelow = (tabId: string, description: string) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, descriptionBelow: description } : t)));
  };

  const updateTabDescriptionBelowAttributeId = (tabId: string, attributeId: number | undefined) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId
          ? { ...t, descriptionBelowAttributeId: attributeId, descriptionBelow: attributeId ? undefined : t.descriptionBelow }
          : t,
      ),
    );
  };

  const enableManualDescriptionBelow = (tabId: string) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId ? { ...t, descriptionBelow: t.descriptionBelow || "", descriptionBelowAttributeId: undefined } : t,
      ),
    );
  };

  const removeTabDescriptionBelow = (tabId: string) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId ? { ...t, descriptionBelow: undefined, descriptionBelowAttributeId: undefined } : t,
      ),
    );
  };

  const updateBuiltInTabSettings = (tabId: string, settings: BuiltInTabConfig["settings"]) => {
    const cleaned = settings
      ? Object.fromEntries(Object.entries(settings).filter(([, v]) => v !== undefined))
      : undefined;
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId && t.kind === "built-in"
          ? { ...t, settings: cleaned && Object.keys(cleaned).length > 0 ? (cleaned as BuiltInTabConfig["settings"]) : undefined }
          : t,
      ),
    );
  };

  const startEditLabel = (tab: TabConfig) => {
    setEditingLabel(tab.id);
  };

  const finishEditLabel = () => {
    setEditingLabel(null);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleFormSubmit)} className="grid gap-4">
        <div>
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Zakładki</h4>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="course-tabs-list">
              {(provided: DroppableProvided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                  {tabs.map((tab, index) => (
                    <Draggable key={tab.id} draggableId={tab.id} index={index}>
                      {(dragProvided: DraggableProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className="rounded-lg border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800"
                        >
                          <div className="flex items-center gap-2 px-3 py-2">
                            <div
                              {...dragProvided.dragHandleProps}
                              className="cursor-grab text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            >
                              <MdDragIndicator size={20} />
                            </div>

                            {editingLabel === tab.id ? (
                              <input
                                type="text"
                                value={tab.label}
                                onChange={(e) => updateTabLabel(tab.id, e.target.value)}
                                onBlur={finishEditLabel}
                                onKeyDown={(e) => e.key === "Enter" && finishEditLabel()}
                                autoFocus
                                className="min-w-0 flex-1 rounded border border-blue-300 px-2 py-0.5 text-sm dark:border-blue-600 dark:bg-slate-700 dark:text-slate-200"
                              />
                            ) : (
                              <span
                                className="flex-1 cursor-pointer text-sm text-slate-700 hover:text-blue-600 dark:text-slate-300 dark:hover:text-blue-400"
                                onClick={() => startEditLabel(tab)}
                                title="Kliknij, aby edytować nazwę"
                              >
                                {tab.label}
                              </span>
                            )}

                            <span
                              className={`rounded px-1.5 py-0.5 text-xs ${
                                tab.kind === "built-in"
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                  : tab.kind === "cards"
                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                    : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                              }`}
                            >
                              {tab.kind === "built-in"
                                ? "Wbudowana"
                                : tab.kind === "cards"
                                  ? "Karty"
                                  : "Niestandardowa"}
                            </span>

                            <button
                              type="button"
                              onClick={() => setEditingTabId(editingTabId === tab.id ? null : tab.id)}
                              className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                              title="Edytuj"
                            >
                              {editingTabId === tab.id ? <MdExpandLess size={18} /> : <MdEdit size={18} />}
                            </button>

                            <button
                              type="button"
                              onClick={() => removeTab(index)}
                              className="rounded p-1 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                              title="Usuń zakładkę"
                            >
                              <MdDelete size={18} />
                            </button>
                          </div>

                          {editingTabId === tab.id && (
                            <>
                              <DescriptionEditor
                                tabId={tab.id}
                                label="Opis (wyświetlany nad treścią zakładki)"
                                currentAttributeId={tab.descriptionAttributeId}
                                currentText={tab.description}
                                onChangeDescription={(html) => updateTabDescription(tab.id, html)}
                                onChangeAttributeId={(attrId) => updateTabDescriptionAttributeId(tab.id, attrId)}
                                onEnableManual={() => enableManualDescription(tab.id)}
                                onRemoveDescription={() => removeTabDescription(tab.id)}
                              />

                              <DescriptionEditor
                                tabId={tab.id}
                                label="Opis (wyświetlany pod treścią zakładki)"
                                currentAttributeId={tab.descriptionBelowAttributeId}
                                currentText={tab.descriptionBelow}
                                onChangeDescription={(html) => updateTabDescriptionBelow(tab.id, html)}
                                onChangeAttributeId={(attrId) => updateTabDescriptionBelowAttributeId(tab.id, attrId)}
                                onEnableManual={() => enableManualDescriptionBelow(tab.id)}
                                onRemoveDescription={() => removeTabDescriptionBelow(tab.id)}
                              />

                              {tab.kind === "custom" && (
                                <div className="border-t border-slate-200 p-3 dark:border-slate-600">
                                  <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                                    Treść zakładki
                                  </label>
                                  <PaziaQuill
                                    value={(tab as CustomTabConfig).content}
                                    onChange={(html) => updateCustomTabContent(tab.id, html)}
                                  />
                                </div>
                              )}

                              {tab.kind === "cards" && (
                                <CardsTabEditor
                                  tab={tab as CardsTabConfig}
                                  onAddCard={() => addCardItem(tab.id)}
                                  onRemoveCard={(cardIdx) => removeCardItem(tab.id, cardIdx)}
                                  onUpdateCard={(cardIdx, field, value) => updateCardItem(tab.id, cardIdx, field, value)}
                                />
                              )}

                              {tab.kind === "built-in" && tab.id === "trainers" && (
                                <div className="border-t border-slate-200 p-3 dark:border-slate-600">
                                  <div className="space-y-3">
                                    <div>
                                      <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                                        Tryb wyświetlania
                                      </label>
                                      <select
                                        value={
                                          (tab as BuiltInTabConfig).settings?.compactMode === true
                                            ? "compact"
                                            : (tab as BuiltInTabConfig).settings?.compactMode === false
                                              ? "standard"
                                              : "auto"
                                        }
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          updateBuiltInTabSettings(tab.id, {
                                            ...(tab as BuiltInTabConfig).settings,
                                            compactMode:
                                              val === "compact" ? true : val === "standard" ? false : undefined,
                                          });
                                        }}
                                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                                      >
                                        <option value="auto">Automatyczny</option>
                                        <option value="standard">Standardowy</option>
                                        <option value="compact">Kompaktowy</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                                        Maks. wyświetlanych (puste = pokaż wszystkich)
                                      </label>
                                      <input
                                        type="number"
                                        min={1}
                                        value={(tab as BuiltInTabConfig).settings?.maxVisible ?? ""}
                                        onChange={(e) => {
                                          const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
                                          updateBuiltInTabSettings(tab.id, {
                                            ...(tab as BuiltInTabConfig).settings,
                                            maxVisible: val,
                                          });
                                        }}
                                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}

                              {tab.kind === "built-in" && tab.id === "schedule" && (
                                <div className="border-t border-slate-200 p-3 dark:border-slate-600">
                                  <div>
                                    <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                                      Wariant
                                    </label>
                                    <select
                                      value={(tab as BuiltInTabConfig).settings?.variant ?? "carousel"}
                                      onChange={(e) => {
                                        const val = e.target.value as "carousel" | "accordion" | "simple-list";
                                        updateBuiltInTabSettings(tab.id, {
                                          ...(tab as BuiltInTabConfig).settings,
                                          variant: val,
                                        });
                                      }}
                                      className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                                    >
                                      <option value="carousel">Karuzela</option>
                                      <option value="accordion">Akordeon</option>
                                      <option value="simple-list">Prosta lista</option>
                                    </select>
                                  </div>
                                </div>
                              )}

                              {tab.kind === "built-in" && tab.id === "program" && (
                                <div className="border-t border-slate-200 p-3 dark:border-slate-600">
                                  <div>
                                    <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                                      Wariant wyświetlania programu
                                    </label>
                                    <select
                                      value={(tab as BuiltInTabConfig).settings?.programVariant ?? "accordion"}
                                      onChange={(e) => {
                                        const val = e.target.value as "accordion" | "modal" | "static-list";
                                        updateBuiltInTabSettings(tab.id, {
                                          ...(tab as BuiltInTabConfig).settings,
                                          programVariant: val,
                                        });
                                      }}
                                      className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                                    >
                                      <option value="accordion">Akordeon (rozwijany)</option>
                                      <option value="modal">Modal (szczegóły w oknie)</option>
                                      <option value="static-list">Lista statyczna</option>
                                    </select>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="mt-3 flex gap-2">
            {availableBuiltIns.length > 0 && (
              <div className="relative">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      addBuiltInTab(e.target.value);
                      e.target.value = "";
                    }
                  }}
                  defaultValue=""
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 transition-colors hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
                >
                  <option value="" disabled>
                    + Dodaj wbudowaną
                  </option>
                  {availableBuiltIns.map((bt) => (
                    <option key={bt.id} value={bt.id}>
                      {bt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              type="button"
              onClick={addCustomTab}
              className="flex items-center gap-1 rounded-lg border border-dashed border-purple-300 px-3 py-1.5 text-sm text-purple-600 transition-colors hover:border-purple-400 hover:bg-purple-50 dark:border-purple-600 dark:text-purple-400 dark:hover:bg-purple-900/20"
            >
              <MdAdd size={16} />
              Dodaj niestandardową
            </button>

            {!hasCardsTab && (
              <button
                type="button"
                onClick={addCardsTab}
                className="flex items-center gap-1 rounded-lg border border-dashed border-green-300 px-3 py-1.5 text-sm text-green-600 transition-colors hover:border-green-400 hover:bg-green-50 dark:border-green-600 dark:text-green-400 dark:hover:bg-green-900/20"
              >
                <MdAdd size={16} />
                Dodaj zakładkę kart
              </button>
            )}
          </div>
        </div>

        <PaziaSelect name="variant" label="Wariant wyświetlania" options={VARIANT_OPTIONS} required />

        <PaziaSelect name="tabsLayout" label="Układ zakładek (mobile)" options={TABS_LAYOUT_OPTIONS} required />

        <PaziaCheckbox name="showSectionHeaders" label="Pokaż nagłówki sekcji" />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton allowCleanSubmit disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

interface CardsTabEditorProps {
  tab: CardsTabConfig;
  onAddCard: () => void;
  onRemoveCard: (cardIdx: number) => void;
  onUpdateCard: (cardIdx: number, field: keyof CardItemConfig, value: string | number | undefined) => void;
}

const CardsTabEditor = ({ tab, onAddCard, onRemoveCard, onUpdateCard }: CardsTabEditorProps) => {
  const { allAttributes, isLoading: loadingAttributes } = useCourseGroupAttributesAll();

  return (
    <div className="border-t border-slate-200 p-3 dark:border-slate-600">
      <div className="space-y-4">
        {tab.cards.map((card, cardIdx) => {
          const isAttribute = !!card.attributeId;
          return (
            <div key={cardIdx} className="rounded-lg border border-slate-200 p-3 dark:border-slate-600">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Karta {cardIdx + 1}</span>
                <button
                  type="button"
                  onClick={() => onRemoveCard(cardIdx)}
                  className="rounded p-1 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                  title="Usuń kartę"
                >
                  <MdDelete size={16} />
                </button>
              </div>

              <div className="mb-2">
                <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Tryb</label>
                <select
                  value={isAttribute ? "attribute" : "static"}
                  onChange={(e) => {
                    const mode = e.target.value;
                    if (mode === "static") {
                      onUpdateCard(cardIdx, "attributeId", undefined);
                    } else {
                      onUpdateCard(cardIdx, "attributeId", allAttributes[0]?.id ?? -1);
                    }
                  }}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                >
                  <option value="static">Statyczna</option>
                  <option value="attribute">Atrybut</option>
                </select>
              </div>

              {isAttribute && (
                <div className="mb-2">
                  <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Atrybut</label>
                  <Select
                    name={`card-attr-${cardIdx}`}
                    options={allAttributes.map((a) => ({ value: a.id, label: a.name }))}
                    value={card.attributeId ?? ""}
                    isClearable={false}
                    isLoading={loadingAttributes}
                    onChange={(opt: IDropdownOption | null) => {
                      onUpdateCard(cardIdx, "attributeId", opt ? Number(opt.value) : undefined);
                    }}
                  />
                </div>
              )}

              <div className="mb-2">
                <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                  Nagłówek {isAttribute && "(opcjonalny, domyślnie nazwa atrybutu)"}
                </label>
                <input
                  type="text"
                  value={card.header}
                  onChange={(e) => onUpdateCard(cardIdx, "header", e.target.value)}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                />
              </div>
              <div className="mb-2 flex gap-3">
                <div>
                  <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Kolor tła</label>
                  <input
                    type="color"
                    value={card.bgColor}
                    onChange={(e) => onUpdateCard(cardIdx, "bgColor", e.target.value)}
                    className="h-8 w-12 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Kolor tekstu</label>
                  <input
                    type="color"
                    value={card.textColor}
                    onChange={(e) => onUpdateCard(cardIdx, "textColor", e.target.value)}
                    className="h-8 w-12 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
                  />
                </div>
              </div>
              {!isAttribute && (
                <div>
                  <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Treść</label>
                  <PaziaQuill value={card.content} onChange={(html) => onUpdateCard(cardIdx, "content", html)} />
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={onAddCard}
          className="flex items-center gap-1 rounded-lg border border-dashed border-green-300 px-3 py-1.5 text-sm text-green-600 transition-colors hover:border-green-400 hover:bg-green-50 dark:border-green-600 dark:text-green-400 dark:hover:bg-green-900/20"
        >
          <MdAdd size={16} />
          Dodaj kartę
        </button>
      </div>
    </div>
  );
};

interface DescriptionEditorProps {
  tabId: string;
  label: string;
  currentAttributeId?: number;
  currentText?: string;
  onChangeDescription: (html: string) => void;
  onChangeAttributeId: (attributeId: number | undefined) => void;
  onEnableManual: () => void;
  onRemoveDescription: () => void;
}

const DescriptionEditor = ({ tabId, label, currentAttributeId, currentText, onChangeDescription, onChangeAttributeId, onEnableManual, onRemoveDescription }: DescriptionEditorProps) => {
  const { allAttributes, isLoading: loadingAttributes } = useCourseGroupAttributesAll();
  const mode = currentAttributeId ? "attribute" : currentText != null ? "manual" : "none";

  return (
    <div className="border-t border-slate-200 p-3 dark:border-slate-600">
      <div className="mb-2 flex items-center justify-between">
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400">
          {label}
        </label>
        <select
          value={mode}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "none") {
              onRemoveDescription();
            } else if (val === "attribute") {
              onChangeAttributeId(allAttributes[0]?.id ?? -1);
            } else {
              onEnableManual();
            }
          }}
          className="rounded border border-slate-300 px-2 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
        >
          <option value="none">Brak</option>
          <option value="manual">Ręcznie</option>
          <option value="attribute">Atrybut</option>
        </select>
      </div>

      {mode === "attribute" && (
        <Select
          name={`desc-attr-${tabId}`}
          options={allAttributes.map((a) => ({ value: a.id, label: a.name }))}
          value={currentAttributeId ?? ""}
          isClearable={false}
          isLoading={loadingAttributes}
          onChange={(opt: IDropdownOption | null) => {
            onChangeAttributeId(opt ? Number(opt.value) : undefined);
          }}
        />
      )}

      {mode === "manual" && (
        <PaziaQuill
          value={currentText ?? ""}
          onChange={onChangeDescription}
        />
      )}
    </div>
  );
};

const VARIANT_OPTIONS = [
  { value: "scroll", label: "Przewijanie (scroll)" },
  { value: "tabs", label: "Zakładki (tabs)" },
];

const TABS_LAYOUT_OPTIONS = [
  { value: "scroll", label: "Poziome przewijanie ze strzałkami (mobile)" },
  { value: "wrap", label: "Zawijanie w wiele wierszy (legacy)" },
];
